/**
 * PaymentsTabBar — shared sticky tab strip used by both the buyer's
 * Payments hub (`/buyer/payments`) and the seller's Payouts hub
 * (`/seller/wallet`), plus the standalone Invoices pages that nest
 * under them (`/buyer/invoices`, `/seller/invoices`).
 *
 * Two flavors of tab:
 *   • hash tabs — stay on the parent route, change `#hash`
 *   • route tab (Invoices) — leaves to a separate page entirely
 *
 * Active state is computed from the current location: when on the
 * Invoices route, the Invoices pill is the lit one even though the
 * other tabs are hash-based. This keeps the visual rhythm consistent
 * regardless of which page within the section the user is on.
 *
 * Caller passes only `role`. Everything else is derived.
 */
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3, Wallet, Landmark, CreditCard, Activity, FileText,
  RefreshCw, Building2,
} from "lucide-react";


const BUYER_TABS = [
  { kind: "hash",  key: "overview",   label: "Overview",   icon: BarChart3 },
  { kind: "hash",  key: "wallet",     label: "Wallet",     icon: Wallet },
  { kind: "hash",  key: "credit",     label: "Credit",     icon: Landmark },
  { kind: "hash",  key: "pre-credit", label: "Pre-credit", icon: Landmark },
  { kind: "route", key: "invoices",   label: "Invoices",   icon: FileText, to: "/buyer/invoices" },
  { kind: "hash",  key: "methods",    label: "Methods",    icon: CreditCard },
  { kind: "hash",  key: "activity",   label: "Activity",   icon: RefreshCw },
];

const SELLER_TABS = [
  { kind: "hash",  key: "overview",   label: "Overview",   icon: BarChart3 },
  { kind: "hash",  key: "wallet",     label: "Wallet",     icon: Wallet },
  { kind: "hash",  key: "credit",     label: "Credit",     icon: CreditCard },
  { kind: "route", key: "invoices",   label: "Invoices",   icon: FileText, to: "/seller/invoices" },
  { kind: "hash",  key: "methods",    label: "Methods",    icon: Building2 },
  { kind: "hash",  key: "activity",   label: "Activity",   icon: Activity },
];


export default function PaymentsTabBar({ role }) {
  const nav = useNavigate();
  const loc = useLocation();
  const tabs = role === "seller" ? SELLER_TABS : BUYER_TABS;
  const parentRoute = role === "seller" ? "/seller/wallet" : "/buyer/payments";
  const defaultHash = "overview";

  const onRouteTab = (loc.pathname === "/seller/invoices" || loc.pathname === "/buyer/invoices");
  const currentHash = (loc.hash || `#${defaultHash}`).replace("#", "");

  const stickyTop = role === "seller" ? "top-0" : "top-[88px]";
  const px = role === "seller" ? "px-8" : "px-12";

  return (
    <div
      className={`sticky ${stickyTop} z-10 bg-background/85 backdrop-blur border-b border-border ${px} py-3`}
      data-testid={`${role}-payments-tabbar`}
    >
      <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1">
        {tabs.map(t => {
          const active = t.kind === "route"
            ? (onRouteTab && loc.pathname === t.to)
            : (!onRouteTab && currentHash === t.key);
          const handleClick = () => {
            if (t.kind === "route") {
              nav(t.to);
            } else {
              nav(`${parentRoute}#${t.key}`);
            }
          };
          return (
            <button
              key={t.key}
              onClick={handleClick}
              data-testid={`${role}-payments-tab-${t.key}`}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full text-sm px-4 py-1.5 border transition-all ${
                active
                  ? "bg-foreground text-background border-foreground"
                  : "bg-secondary border-foreground/15 text-foreground/70 hover:bg-foreground/10"
              }`}
            >
              <t.icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
